import { checkOnlineStatus, isSafari } from '@woonstad/helpers-utils';
import { useEffect, useState } from 'react';

interface Props {
  fallbackUrl: string;
  checkInterval?: number;
}

/**
 * useIsOnline hook
 * - browser that support "online" and "offline" events should use those events
 * - Checks if the user is online by polling the current page or a external url
 * **/
export function useIsOnline(settings: Props = {
  fallbackUrl: 'https://www.woonstadrotterdam.nl',
}) {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    if (isSafari()) {
      // Since Safari doesn't support online / offline events we have to poll a url to detect if we are online.
      // For online environments we can itself, since localhost will always be online even when offline we
      // connect to an external url, in this case check the woonstad website
      const url = !window?.origin?.includes('localhost') ? `${window.origin}/${window.location.pathname}` : settings.fallbackUrl;

      const intervalId = setInterval(() => checkOnlineStatus(url, (status) => {
        if (!isOnline && status) {
          setIsOnline(true);
        }

        if (isOnline && !status) {
          setIsOnline(false);
        }
      }), settings.checkInterval ?? 5000);

      return () => {
        clearInterval(intervalId);
      };
    }

    // For normal browsers use the online/offline events to toggle the online/offline switch
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      // remove event handlers when unmounting
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };

  }, [settings, isOnline]);

  return {
    online: isOnline,
  };
}
